import type { Condition, ExpressionNode } from '~/types/filters'

export interface ScopeIds {
  routeIds: number[]
  groupIds: number[]
  zoneIds: number[]
}

export interface ExtractedScope extends ScopeIds {
  siteIds: number[]
  supported: boolean
}

const SCOPE_OPERANDS: Record<string, keyof ScopeIds> = {
  routeId: 'routeIds',
  groupId: 'groupIds',
  zoneId: 'zoneIds'
}

const SITE_OPERAND = 'siteId'

export interface ExtractScopeOptions {
  sites?: boolean
}

const INCLUSIVE_OPERATORS: string[] = ['=', 'IN']

const toIds = (value: Condition['value']): number[] | null => {
  const values = Array.isArray(value) ? value : [value]
  const ids = values.map((entry) => Number(entry))
  return ids.every((id) => Number.isInteger(id) && id > 0) ? ids : null
}

const collect = (
  node: ExpressionNode | null | undefined,
  scope: ExtractedScope,
  options: ExtractScopeOptions
): void => {
  if (!node) return
  for (const condition of node.conditions ?? []) {
    const isSite = options.sites === true && condition.operand === SITE_OPERAND
    const bucket = isSite ? 'siteIds' : SCOPE_OPERANDS[condition.operand]
    const ids = bucket ? toIds(condition.value) : null
    if (
      !bucket ||
      condition.operandType !== 'OBJ_ATTRIBUTE' ||
      !INCLUSIVE_OPERATORS.includes(condition.operator) ||
      !ids
    ) {
      scope.supported = false
      continue
    }
    for (const id of ids) if (!scope[bucket].includes(id)) scope[bucket].push(id)
  }
  for (const child of node.subNodes ?? []) collect(child, scope, options)
}

export const extractScopeIds = (
  conditions: ExpressionNode | null | undefined,
  options: ExtractScopeOptions = {}
): ExtractedScope => {
  const scope: ExtractedScope = { routeIds: [], groupIds: [], zoneIds: [], siteIds: [], supported: true }
  collect(conditions, scope, options)
  return scope
}
